import { LitElement, css, html, nothing } from 'lit-all'
import { generateSrcSet, formatCurrency, t } from 'lib-utils'
class ASVariantCard extends LitElement {
  static properties = {
    variant: { type: Object, attribute: 'variant' },
    layout: { type: String },
    complementaryProducts: { type: Boolean },
    collectionHandle: { type: String, attribute: 'collection-handle' },
  }

  constructor() {
    super()

    this.settings = window.__as_product_card_settings
  }

  createRenderRoot() {
    return this
  }

  connectedCallback() {
    super.connectedCallback()

    this.setVariantItemClasses()
  }

  updated(changedProperties) {
    if (changedProperties.has('layout')) {
      this.setVariantItemClasses()
    }
  }

  // Building class list for the product item
  setVariantItemClasses() {
    const classList = [
      'product-item',
      this.layout === 'grid' ? `product-item--vertical ${this.settings.grid_classes}` : 'product-item--list ',
      this.complementaryProducts ? 'product-item--complementary' : '',
    ].join(' ')

    this.className = classList
  }

  // Processing product labels
  get variantLabels() {
    if (!this.variant.tags) return []

    return this.variant.tags
      .filter((tag) => tag.includes('__label:'))
      .map((tag) => tag.split('__label:').pop())
      .map(
        (label) =>
          html`<span class="product-label product-label--custom1"
            >${label}</span
          >`
      )
  }

  // Calculate savings
  get savings() {
    if (
      !this.settings.showDiscount ||
      this.variant.price >= this.variant.compareAtPrice
    ) {
      return null
    }
    return this.settings.discountMode === 'percentage'
      ? `${Math.round(
        ((this.variant.compareAtPrice - this.variant.price) * 100) /
        this.variant.compareAtPrice
      )}%`
      : html`<span>${this.variant.compareAtPrice - this.variant.price}</span>`
  }

  renderMedia() {
    // Check if product has media
    if (!this.variant.image || !this.variant.image.url) return null

    const primaryMedia = this.variant.image
    const paddingBottom = 100.0 / (primaryMedia.width / primaryMedia.height)

    // Determine classes for aspect ratio
    const aspectRatioClass =
      this.settings.product_image_size !== 'natural'
        ? `aspect-ratio--${this.settings.product_image_size}`
        : ''

    // Construct primary image
    const primaryImage = html`
      <img
        src="${primaryMedia.url}"
        width="${primaryMedia.width}"
        loading="lazy"
        sizes="(max-width: 699px) 100vw, 600px"
        srcset="${generateSrcSet(primaryMedia)}"
        class="product-item__primary-image"
        data-media-id="${primaryMedia.id}"
        alt="${primaryMedia.alt}"
      />
    `

    return html`
      <a
        href="${this.collectionHandle ? `/collections/${this.collectionHandle}` : ''}/products/${this.variant.handle}?variant=${this.variant.id}"
        class="product-item__image-wrapper"
      >
        <div
          class="aspect-ratio ${aspectRatioClass}"
          style="padding-bottom: ${paddingBottom}%"
        >
          ${primaryImage}
        </div>
      </a>
    `
  }


  renderVendor() {
    if (!this.settings.show_vendor) return

    const vendorUrl = `/collections/${this.variant.vendor}`
    return html`<a class="product-item__vendor link" href="${vendorUrl}">${this.variant.vendor}</a>`
  }

  renderProductInfo() {
    const showVendorAfterTitle = this.settings.product_vendor_position === 'after_title'
    const variantTitle =
      this.variant.title !== 'Default Title' ? ` | ${this.variant.title}` : ''
    const productTitle = new DOMParser().parseFromString(
      this.variant.productTitle,
      'text/html'
    ).documentElement.textContent

    return html`
      ${!showVendorAfterTitle ? this.renderVendor() : nothing}

      <a
        href="${this.collectionHandle ? `/collections/${this.collectionHandle}` : ''}/products/${this.variant.handle}?variant=${this.variant.id}"
        class="product-item__title text--strong link"
        >${productTitle}${variantTitle}</a
      >

      ${showVendorAfterTitle ? this.renderVendor() : nothing}
    `
  }

  renderInventoryStatus() {
    if (
      !this.settings.showInventoryQuantity ||
      !this.variant.variants?.length ||
      this.variant.templateSuffix === 'pre-order') {
      return
    }

    if (!this.variant.available) {
      return html`<span class="product-item__inventory inventory">Sold out</span>`
    }

    // For products with 'continue' policy or no inventory management
    if (this.variant.inventoryPolicy === 'continue' && this.variant.inventoryQuantity <= 0) {
      return html`<span class="product-item__inventory inventory inventory--high">Available on backorder</span>`
    }

    return html`<span class="product-item__inventory inventory inventory--high">In stock</span>`
  }

  render() {
    return html`
      ${this.variantLabels.length
        ? html`<div class="product-item__label-list">${this.variantLabels}</div>`
        : null}

        ${this.renderMedia()}

      <div class="product-item__info">
        <div class="product-item__info-inner">
          ${this.renderProductInfo()}

          ${`` // <as-product-rating product="${this.variant}" />
      }

          ${this.renderInventoryStatus()}
        </div>
      </div>

      <as-variant-card-actions
        .config=${{ showQuickView: this.settings.show_quick_view, showQuickBuy: this.settings.show_quick_buy }}
        .showAddToCart=${true}
        .variant=${this.variant}
        .complementaryProducts=${false}
      ></as-variant-card-actions>
    `
  }

  static styles = css``
}

customElements.define('as-variant-card', ASVariantCard)
